import { memo, useMemo } from "react"
import { NotOnMobile, OnMobile, useMobile } from "lib/@components/mobile"
import { Box, Stack, Typography } from "@mui/material"
import {
    ClassificationArea,
    SkillSetArea,
    TaskRowEnd,
    TaskRowStart,
    TaskTitle,
    TimeAndFrequency,
    UOMArea,
} from "slot-definitions"
import { MinHeightBox } from "lib/@components/minHeightBox"
import { Editable } from "lib/@components/editable"
import { BoundPopup } from "lib/@components/combos/boundPopup"
import { SummaryDataChanged } from "event-definitions"
import { VALID_CLASSIFICATIONS } from "library/SFG20/constants.mjs"
import { Classification } from "routes/schedule/tasks/classification"
import { HighlightBox } from "lib/@components/highlightBox"
import { BoundEditableText } from "lib/@components/editable/editableText"
import { Time } from "routes/schedule/tasks/time"
import { Frequency } from "routes/schedule/tasks/frequency"
import { UnitLabel } from "routes/schedule/tasks/unit-label"
import { SkillSet } from "routes/schedule/tasks/skill-set"
import { editorSx, indexSx, mobileSx, textSx, wrapSx } from "routes/schedule/tasks/styles"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useTranslationContext } from "routes/schedule/plugins/translation/use-translation-context"
import { If } from "lib/@components/switch"

export const TaskRowEntry = memo(function TaskRowEntry({ classification, headerSx, mobile, title, titleDetails }) {
    const { task } = useBoundContext()
    const isMobile = useMobile()
    const { rtl } = useTranslationContext()
    let itemSx = useMemo(
        () => ({
            width: 1,
            display: "flex",
            flexDirection: "row",
            fontSize: isMobile ? "85%" : undefined,
            whiteSpace: "normal",
        }),
        [isMobile]
    )
    const titleTextSx = useMemo(() => (rtl ? { ...textSx, textAlign: "right" } : textSx), [rtl])

    return (
        <>
            <NotOnMobile isMobile={isMobile}>
                <Box flex={1}>
                    <Stack alignItems="center" direction="row" sx={wrapSx}>
                        <TaskRowStart.Slot Container={<Box />} />
                        <MinHeightBox mr={1}>
                            <ClassificationArea.Slot>
                                <Editable
                                    p={0.5}
                                    field="classification"
                                    editor={
                                        <BoundPopup
                                            onChanged={SummaryDataChanged.raiseOnce}
                                            aria-label="Classification"
                                            data-cy="classification-control"
                                            field="classification"
                                            contentProps={{ pt: 1 }}
                                            options={VALID_CLASSIFICATIONS}
                                        >
                                            <Classification classification={classification} />
                                        </BoundPopup>
                                    }
                                >
                                    <HighlightBox path="classification" data-comment-part="classification">
                                        <Classification classification={classification} />
                                    </HighlightBox>
                                </Editable>
                            </ClassificationArea.Slot>
                        </MinHeightBox>
                        <MinHeightBox sx={rtl ? { marginInlineEnd: 1 } : undefined}>
                            <Typography sx={indexSx} component="div" className="task-row-entry-index">
                                {title}
                                <If value={title}>.</If>
                            </Typography>
                        </MinHeightBox>
                        <MinHeightBox sx={{ flex: 1 }} data-comment-part="title">
                            <Typography sx={itemSx} component="div" data-cy="schedule-task-title-text">
                                <TaskTitle.Slot>
                                    <HighlightBox width={1} flex={1} path="title">
                                        <BoundEditableText
                                            outerSx={{ width: 1 }}
                                            sx={titleTextSx}
                                            field="title"
                                            preserveHtml
                                            data-cy="schedule-task-title"
                                        />
                                    </HighlightBox>
                                </TaskTitle.Slot>
                            </Typography>
                        </MinHeightBox>

                        {VALID_CLASSIFICATIONS.includes(classification) && (
                            <Stack direction="row" alignItems="center">
                                <TimeAndFrequency.Slot>
                                    <Time />
                                    <Frequency />
                                </TimeAndFrequency.Slot>
                                <UOMArea.Slot>
                                    <UnitLabel />
                                </UOMArea.Slot>
                                <SkillSetArea.Slot>
                                    <SkillSet />
                                </SkillSetArea.Slot>
                            </Stack>
                        )}
                    </Stack>
                </Box>
            </NotOnMobile>
            <OnMobile isMobile={mobile}>
                <Stack width={1} sx={mobileSx}>
                    <Stack alignItems="flex-start" spacing={0} direction="row">
                        <ClassificationArea.Slot>
                            <Editable
                                p={0.5}
                                field="classification"
                                editor={
                                    <BoundPopup
                                        onChanged={SummaryDataChanged.raiseOnce}
                                        contentProps={{ pt: 1 }}
                                        aria-label="Classification"
                                        data-cy="classification-control"
                                        field="classification"
                                        options={VALID_CLASSIFICATIONS}
                                    >
                                        <Classification classification={classification} />
                                    </BoundPopup>
                                }
                            >
                                <HighlightBox path="classification" data-comment-part="classification" mr={1}>
                                    <Classification classification={classification} />
                                </HighlightBox>
                            </Editable>
                        </ClassificationArea.Slot>
                        <TaskRowStart.Slot />

                        <Box>
                            <Typography sx={headerSx} component="h2">
                                {title}.
                            </Typography>
                        </Box>
                        <Box data-comment-part="title">
                            <Typography sx={titleDetails} component="h2">
                                <TaskTitle.Slot>
                                    <BoundEditableText field="title" preserveHtml />
                                </TaskTitle.Slot>
                            </Typography>
                        </Box>
                    </Stack>
                    {VALID_CLASSIFICATIONS.includes(classification) && (
                        <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={0} sx={editorSx}>
                            <TimeAndFrequency.Slot>
                                <Time />
                                <Frequency />
                            </TimeAndFrequency.Slot>
                            <UOMArea.Slot>
                                <UnitLabel />
                            </UOMArea.Slot>
                            <SkillSetArea.Slot>
                                <SkillSet describe />
                            </SkillSetArea.Slot>
                        </Stack>
                    )}
                </Stack>
            </OnMobile>
            <TaskRowEnd.Slot Container={<MinHeightBox />} item={task} />
        </>
    )
})
